// src/pages/terms/TermsManagePage.tsx
import * as React from "react";
import useSWR from "swr";

import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { snackbarStore } from "src/store/snackbarStore";
import {
  useTermsType,
  useTermsStatus,
  useTermsApplyStrDatetime,
  useTermsApplyEndDatetime,
  useTermsPage,
  useTermsSize,
  useTermsOrder,
  useTermsSearchTick,
  useSetTermsQuery,
  useResetTermsQuery,
  useTriggerTermsSearch,
  type TermsItem,
} from "src/store/terms";

import {
  getTermsListApi,
  createTermsApi,
  updateTermsApi,
  deleteTermsApi,
} from "src/api/terms/termsApi";

import TermsFilterBar from "src/components/terms/TermsFilterBar";
import TermsEditModal from "src/components/terms/TermsEditModal";

const SIZE_OPTIONS = [10, 20, 25, 50, 100];

export default function TermsManagePage() {
  const termsType = useTermsType();
  const termsStatus = useTermsStatus();
  const applyStrDatetime = useTermsApplyStrDatetime();
  const applyEndDatetime = useTermsApplyEndDatetime();

  const page = useTermsPage(); // UI 1-based
  const size = useTermsSize();
  const order = useTermsOrder();
  const searchTick = useTermsSearchTick();

  const setQuery = useSetTermsQuery();
  const resetQuery = useResetTermsQuery();
  const triggerSearch = useTriggerTermsSearch();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<TermsItem | null>(null);
  const [saving, setSaving] = React.useState(false);

  const swrKey = React.useMemo(
    () =>
      [
        "terms/list",
        searchTick,
        termsType,
        termsStatus,
        applyStrDatetime,
        applyEndDatetime,
        page,
        size,
        order,
      ] as const,
    [searchTick, termsType, termsStatus, applyStrDatetime, applyEndDatetime, page, size, order]
  );

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getTermsListApi({
        termsType: termsType || undefined,
        termsStatus: termsStatus || undefined,
        applyStrDatetime: applyStrDatetime || undefined,
        applyEndDatetime: applyEndDatetime || undefined,
        page: apiPage,
        size,
        order,
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const list = data?.data?.content ?? [];
  const totalPages = data?.data?.page?.totalPages ?? 1;
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const openCreate = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const openEdit = (item: TermsItem) => {
    setSelected(item);
    setModalOpen(true);
  };

  const onSubmit = async (payload: Partial<TermsItem> & { termsId?: number }): Promise<void> => {
    try {
      setSaving(true);

      const res =
        payload.termsId != null
          ? await updateTermsApi(payload.termsId, payload)
          : await createTermsApi(payload);

      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "저장에 실패했습니다.",
          severity: "error",
        });
        return;
      }

      snackbarStore.getState().addSnackbar({
        message: res.message || "저장되었습니다.",
        severity: "success",
      });

      setModalOpen(false);
      setSelected(null);
      await mutate();
    } catch (e: any) {
      snackbarStore.getState().addSnackbar({
        message: e?.message || "저장 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item: TermsItem) => {
    const ok = window.confirm(`"${item.termsTtl ?? item.termsId}" 삭제할까?`);
    if (!ok) return;

    try {
      setSaving(true);

      const res = await deleteTermsApi(item.termsId);

      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "삭제에 실패했습니다.",
          severity: "error",
        });
        return;
      }

      snackbarStore.getState().addSnackbar({
        message: res.message || "삭제 완료",
        severity: "success",
      });

      await mutate();
    } catch (e: any) {
      snackbarStore.getState().addSnackbar({
        message: e?.message || "삭제 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                이용약관 목록
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                총 {totalElements.toLocaleString()}건
              </Typography>
            </Stack>

            {/* <Button variant="contained" onClick={openCreate} disabled={saving}>
              등록
            </Button> */}
          </Stack>

          {/* 필터바(너가 이미 만들었던거) */}
          <TermsFilterBar
            termsType={termsType}
            termsStatus={termsStatus}
            applyStrDatetime={applyStrDatetime}
            applyEndDatetime={applyEndDatetime}
            order={order}
            onChange={(next) => setQuery({ ...next, page: 1 })}
            onSearch={() => triggerSearch()}
            onReset={() => resetQuery()}
            onCreate={openCreate}
          />

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(error as any)?.message ?? "약관 목록 로드 실패"}
            </Alert>
          ) : null}

          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={90}>ID</TableCell>
                    <TableCell width={120}>Type</TableCell>
                    <TableCell width={120}>제목</TableCell>
                    <TableCell width={140}>Status</TableCell>
                    {/* <TableCell width={120}>Order</TableCell>
                    <TableCell width={180}>시작일</TableCell>
                    <TableCell width={180}>종료일</TableCell> */}
                    <TableCell width={120} align="right">
                      액션
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {list.map((it) => (
                    <TableRow key={it.termsId} hover>
                      <TableCell>{it.termsId}</TableCell>
                      <TableCell>{it.termsType ?? "-"}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{it.termsTtl ?? "-"}</TableCell>
                      <TableCell>{it.termsStatus ?? "-"}</TableCell>
                      {/* <TableCell>{Number.isFinite(it.termsOrder) ? it.termsOrder : "-"}</TableCell>
                      <TableCell>
                        {it.applyStrDatetime ? new Date(it.applyStrDatetime).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell>
                        {it.applyEndDatetime ? new Date(it.applyEndDatetime).toLocaleString() : "-"}
                      </TableCell> */}
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(it)} disabled={saving}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(it)} disabled={saving}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {list.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        데이터가 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>

              <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
                <Pagination
                  count={Math.max(1, totalPages)}
                  page={page}
                  onChange={(_, nextPage) => setQuery({ page: nextPage })}
                  color="primary"
                  shape="rounded"
                  disabled={isLoading || saving}
                />
              </Stack>
            </>
          )}
        </Paper>
      </Box>

      <TermsEditModal
        open={modalOpen}
        mode={selected ? "edit" : "create"}
        item={selected}
        onClose={() => {
          setModalOpen(false);
          setSelected(null);
        }}
        onSubmit={onSubmit}
      />
    </>
  );
}
